import { useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Alternador, Entrada, Rotulo, Selecao, EntradaSensivel } from '../../componentes/formulario';
import { nomeDaEmpresa, useEmpresasDoCatalogo, type EmpresaCatalogo } from '../../componentes/verticais';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, get, mensagemDeErro } from '../../nucleo/api';
import { useSessao } from '../../nucleo/sessao';
import { dinheiro, numero, numeroEditavel, paraNumero, textoLivre } from '../../nucleo/formato';
import { bruto, converterDesconto, descontoEmCentavos, erroDoDesconto, percentualDoDesconto, type TipoDesconto } from './crm-desconto';
import { RECORRENCIAS, type ItemOportunidade, type Oportunidade, type Recorrencia } from './tipos';
import { ofereceNa, servicosDaVertical, verticaisDoItem } from './vertical-item';
import { FormServicoCatalogo, TODAS, useCatalogoVenda, useGravarServico, type ServicoCatalogo } from './FormServicoCatalogo';

/**
 * CRM no novo desenho, lote 4: aba Produtos da ficha da negociação (o RD). Lista com nome e recorrência, quantidade, preço e
 * desconto, subtotal; "Adicionar produto ou serviço" em dois passos (escolher no catálogo, configurar) com o desconto em % ou
 * R$ (complemento 2). Val1 (28/10) e crm8 (29/10, 2a): o passo 1 começa pela VERTICAL, entre as marcadas para o lead (a
 * negociação ou a empresa dela), e o catálogo mostra só os serviços oferecidos nela (ou em "Todas"); uma só já vem escolhida. Grava a lista inteira (`PUT /pipeline/oportunidades/:id/itens`); o valor da negociação vira a soma.
 */

const totalDoItem = (i: ItemOportunidade) => bruto(i.quantidade, i.preco_centavos) - (i.desconto_centavos ?? 0);
const paraEnvio = (i: ItemOportunidade) => ({
  servico_id: i.servico_id,
  descricao: i.descricao,
  quantidade: i.quantidade,
  preco_centavos: i.preco_centavos,
  recorrencia: i.recorrencia,
  vertical_id: i.vertical_id ?? null,
  desconto_tipo: i.desconto_tipo ?? 'valor',
  desconto_percentual: i.desconto_percentual ?? null,
  desconto_centavos: i.desconto_centavos ?? 0,
});

export function AbaProdutos({ op, podeEditar }: { op: Oportunidade; podeEditar: boolean }) {
  const { t } = useTranslation('pipeline');
  const qc = useQueryClient();
  const toast = useToast();
  const itens = op.itens ?? [];
  const idAba = useId();
  // Lista de 30/10 (B), item 16: "Criar e adicionar outro" grava e reabre o formulário limpo (nova `chave`), na mesma vertical.
  const [editando, setEditando] = useState<{ indice: number | null; chave?: number; vertical?: string } | null>(null);
  // A lista que vale para a próxima gravação: a do servidor ou, logo depois de gravar, a gravada (a recarga ainda não chegou).
  const ultima = useRef<{ base: ItemOportunidade[] | undefined; lista: ItemOportunidade[] } | null>(null);
  const atuais = () => (ultima.current && ultima.current.base === op.itens ? ultima.current.lista : itens);
  const [remover, setRemover] = useState<number | null>(null);
  const aberta = op.estado === 'aberta';
  const gravar = useMutation({
    mutationFn: (lista: ItemOportunidade[]) => api('PUT', `/pipeline/oportunidades/${op.id}/itens`, { itens: lista.map(paraEnvio) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['pipeline'] });
      await qc.invalidateQueries({ queryKey: ['chatter', 'pipeline.oportunidade', op.id] });
    },
  });
  const unico = itens.filter((i) => i.recorrencia === 'avulso').reduce((s, i) => s + totalDoItem(i), 0);
  const recorrente = itens.filter((i) => i.recorrencia !== 'avulso').reduce((s, i) => s + totalDoItem(i), 0);
  return (
    <section className="flex flex-col gap-3" data-aba-produtos="">
      {/* Ajustes do CRM (27/10, F13): a faixa do RD com "Exibir: Proposta atual" (os produtos são os da negociação). */}
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`${idAba}-exibir`} className="text-sm font-semibold">
          {t('crm.ficha2.exibir')}
        </label>
        <div className="w-48">
          <Selecao id={`${idAba}-exibir`} value="atual" opcoes={[{ valor: 'atual', rotulo: t('crm.ficha2.propostaAtual') }]} onChange={() => undefined} />
        </div>
        <span className="flex-1" />
        {aberta && podeEditar && (
          <Botao icone={<Plus className="size-4" />} onClick={() => setEditando({ indice: null })} data-adicionar-produto="">
            {t('crm.ficha.produtos.adicionar')}
          </Botao>
        )}
      </div>
      {itens.length === 0 ? (
        <p className="rounded-xl border border-dashed border-borda px-4 py-8 text-center text-sm text-tinta-3">{t('crm.ficha.produtos.vazio')}</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-borda">
          {/* Lote crm6 (item 5): no celular, os itens em cartões (o padrão da Tabela), sem rolagem lateral; a tabela a partir de 768 px. */}
          <ul className="divide-y divide-borda md:hidden" data-produtos-cartoes="">
            {itens.map((i, n) => (
              <li key={i.id} className="flex flex-col gap-1 px-4 py-3 text-sm" data-produto-cartao={n}>
                <span className="flex items-start gap-2">
                  <span className="min-w-0 flex-1">
                    {aberta && podeEditar ? (
                      <button type="button" className="text-left font-semibold text-acento-forte uppercase hover:underline" onClick={() => setEditando({ indice: n })}>
                        {textoLivre(i.descricao)}
                      </button>
                    ) : (
                      <span className="font-semibold uppercase">{textoLivre(i.descricao)}</span>
                    )}
                    <span className="block text-xs text-tinta-3">{t(`itens.recorrencia.${i.recorrencia}`)}</span>
                  </span>
                  {aberta && podeEditar && (
                    <span className="flex shrink-0">
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.produtos.editar', { nome: textoLivre(i.descricao, op.parceiro_nome) })} icone={<Pencil className="size-4" />} onClick={() => setEditando({ indice: n })} />
                      <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.produtos.remover', { nome: textoLivre(i.descricao, op.parceiro_nome) })} icone={<Trash2 className="size-4" />} onClick={() => setRemover(n)} />
                    </span>
                  )}
                </span>
                <dl className="grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <dt className="text-tinta-3">{t('crm.ficha.produtos.colunas.quantidade')}</dt>
                    <dd className="numero text-sm">{numero(i.quantidade, Math.min(4, (String(i.quantidade).split('.')[1] ?? '').length))}</dd>
                  </div>
                  <div>
                    <dt className="text-tinta-3">{t('crm.ficha.produtos.colunas.preco')}</dt>
                    <dd className="numero text-sm">{dinheiro(i.preco_centavos, op.moeda)}</dd>
                  </div>
                  <div className="text-right">
                    <dt className="text-tinta-3">{t('crm.ficha.produtos.colunas.subtotal')}</dt>
                    <dd className="numero text-sm font-semibold">{dinheiro(totalDoItem(i), op.moeda)}</dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
          <table className="hidden w-full text-sm md:table">
            <thead>
              <tr className="border-b border-borda bg-sup-2 text-left">
                {(['nome', 'quantidade', 'preco', 'subtotal'] as const).map((c) => (
                  <th key={c} scope="col" className="h-11 px-4 py-2 text-xs font-semibold tracking-wider whitespace-nowrap text-tinta-3 uppercase">
                    {t(`crm.ficha.produtos.colunas.${c}`)}
                  </th>
                ))}
                <th scope="col" className="relative w-24 px-4">
                  <span className="sr-only">{t('crm.ficha.produtos.acoes')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i, n) => (
                <tr key={i.id} className="border-t border-borda" data-produto={n}>
                  <td className="px-4 py-3">
                    {aberta && podeEditar ? (
                      <button type="button" className="text-left font-semibold text-acento-forte uppercase hover:underline" onClick={() => setEditando({ indice: n })}>
                        {textoLivre(i.descricao)}
                      </button>
                    ) : (
                      <span className="font-semibold uppercase">{textoLivre(i.descricao)}</span>
                    )}
                    <span className="block text-xs text-tinta-3">{t(`itens.recorrencia.${i.recorrencia}`)}</span>
                  </td>
                  <td className="numero px-4 py-3">{numero(i.quantidade, Math.min(4, (String(i.quantidade).split('.')[1] ?? '').length))}</td>
                  <td className="numero px-4 py-3">
                    <span className="font-semibold">{dinheiro(i.preco_centavos, op.moeda)}</span>
                    {(i.desconto_centavos ?? 0) > 0 && (
                      <span className="block text-xs text-tinta-3" data-desconto-linha="">
                        {i.desconto_tipo === 'percentual' ? t('crm.ficha.produtos.descontoPct', { pct: numero(i.desconto_percentual ?? 0, 2), valor: dinheiro(i.desconto_centavos ?? 0, op.moeda) }) : t('crm.ficha.produtos.descontoValor', { valor: dinheiro(i.desconto_centavos ?? 0, op.moeda) })}
                      </span>
                    )}
                  </td>
                  <td className="numero px-4 py-3 font-semibold">{dinheiro(totalDoItem(i), op.moeda)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {aberta && podeEditar && (
                      <>
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.produtos.editar', { nome: textoLivre(i.descricao, op.parceiro_nome) })} icone={<Pencil className="size-4" />} onClick={() => setEditando({ indice: n })} />
                        <Botao tamanho="sm" variante="fantasma" aria-label={t('crm.ficha.produtos.remover', { nome: textoLivre(i.descricao, op.parceiro_nome) })} icone={<Trash2 className="size-4" />} onClick={() => setRemover(n)} />
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <dl className="flex flex-wrap justify-end gap-x-6 gap-y-1 border-t border-borda px-4 py-3 text-sm">
            {unico > 0 && recorrente > 0 && (
              <>
                <div className="flex gap-2">
                  <dt className="text-tinta-3">{t('crm.ficha.produtos.unico')}</dt>
                  <dd className="numero">{dinheiro(unico, op.moeda)}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-tinta-3">{t('crm.ficha.produtos.recorrente')}</dt>
                  <dd className="numero">{dinheiro(recorrente, op.moeda)}</dd>
                </div>
              </>
            )}
            <div className="flex gap-2 font-semibold">
              <dt>{t('crm.ficha.produtos.total')}</dt>
              <dd className="numero" data-total-produtos="">
                {dinheiro(unico + recorrente, op.moeda)}
              </dd>
            </div>
          </dl>
        </div>
      )}
      {editando && (
        <FormProduto
          key={editando.chave ?? 0}
          op={op}
          atual={editando.indice === null ? null : itens[editando.indice]!}
          verticalInicial={editando.vertical}
          aoFechar={() => setEditando(null)}
          aoSalvar={async (item, outro) => {
            const base = atuais();
            const lista = editando.indice === null ? [...base, item] : base.map((x, n) => (n === editando.indice ? item : x));
            await gravar.mutateAsync(lista);
            ultima.current = { base: op.itens, lista };
            if (outro) {
              toast.sucesso(t('crm.ficha.produtos.adicionadoOutro'));
              setEditando({ indice: null, chave: (editando.chave ?? 0) + 1, vertical: item.vertical_id ?? undefined });
              return;
            }
            toast.sucesso(editando.indice === null ? t('crm.ficha.produtos.adicionado') : t('crm.ficha.produtos.atualizado'));
            setEditando(null);
          }}
        />
      )}
      <Confirmar
        aberto={remover !== null}
        aoFechar={() => setRemover(null)}
        perigoso
        titulo={t('crm.ficha.produtos.confirmarRemover')}
        texto={remover !== null ? textoLivre(itens[remover]?.descricao, op.parceiro_nome) : ''}
        carregando={gravar.isPending}
        aoConfirmar={() => {
          const n = remover;
          setRemover(null);
          if (n !== null) gravar.mutate(itens.filter((_, j) => j !== n), { onError: (e) => toast.erro(mensagemDeErro(e)) });
        }}
      />
    </section>
  );
}

function FormProduto({
  op,
  atual,
  verticalInicial,
  aoFechar,
  aoSalvar,
}: {
  op: Oportunidade;
  atual: ItemOportunidade | null;
  /** Item 16: a vertical do item anterior, no "Criar e adicionar outro". */
  verticalInicial?: string;
  aoFechar: () => void;
  aoSalvar: (i: ItemOportunidade, outro: boolean) => Promise<void>;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const id = useId();
  const [passo, setPasso] = useState<1 | 2>(atual ? 2 : 1);
  const [servico, setServico] = useState<{ id: string | null; nome: string; preco: number } | null>(atual ? { id: atual.servico_id, nome: atual.descricao, preco: atual.preco_centavos } : null);
  const [quantidade, setQuantidade] = useState(numeroEditavel(atual?.quantidade ?? 1));
  const [preco, setPreco] = useState(atual ? numeroEditavel(atual.preco_centavos / 100, 2) : '');
  const [recorrencia, setRecorrencia] = useState<Recorrencia>(atual?.recorrencia ?? 'avulso');
  const [comDesconto, setComDesconto] = useState(!!atual?.desconto_centavos);
  const [tipo, setTipo] = useState<TipoDesconto>(atual?.desconto_tipo ?? 'percentual');
  const [valorDesconto, setValorDesconto] = useState(() => {
    if (!atual?.desconto_centavos) return '';
    return atual.desconto_tipo === 'percentual' ? numeroEditavel(atual.desconto_percentual ?? 0) : numeroEditavel(atual.desconto_centavos / 100, 2);
  });
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const verticais = useVerticaisDoItem(op);
  const unica = verticais.unica?.id ?? '';
  const [vertical, setVertical] = useState(atual?.vertical_id ?? verticalInicial ?? '');
  const [aviso, setAviso] = useState<string | null>(null);
  const catalogo = useCatalogoVenda();
  // Crm8 (29/10, 2a): uma vertical só possível para o lead já vem escolhida (o seletor some); com mais de uma, escolhe-se primeiro.
  const verticalAtiva = vertical || unica;
  const seletorVisivel = verticais.opcoes.length > 1 || (!!atual?.vertical_id && verticais.opcoes.length > 0 && !verticais.opcoes.some((e) => e.id === atual.vertical_id));
  const exigeVertical = !atual && verticais.opcoes.length > 1 && !verticalAtiva;
  const trocarVertical = (novo: string) => {
    setVertical(novo);
    setErro(null);
    const sv = servico?.id ? catalogo.data?.find((x) => x.id === servico.id) : undefined;
    // Trocar para uma vertical em que o serviço não é oferecido pede escolher o serviço de novo (como na tela antiga).
    if (sv && !ofereceNa(sv, novo)) {
      setServico(null);
      setPasso(1);
      setAviso(t('crm.ficha.produtos.servicoForaDaVertical', { nome: sv.nome }));
    } else setAviso(null);
  };
  const num = (v: string) => paraNumero(v) ?? 0;
  const q = num(quantidade) || 0;
  const pc = Math.round(num(preco) * 100);
  const digitado = comDesconto ? num(valorDesconto) : 0;
  const desconto = descontoEmCentavos(tipo, digitado, q, pc);
  const total = bruto(q, pc);
  const limite = comDesconto ? erroDoDesconto(tipo, digitado, q, pc) : null;
  const equivalencia =
    comDesconto && digitado > 0
      ? tipo === 'percentual'
        ? t('crm.ficha.produtos.equivalePct', { pct: numero(digitado, 2), valor: dinheiro(desconto, op.moeda) })
        : t('crm.ficha.produtos.equivaleValor', { valor: dinheiro(desconto, op.moeda), pct: numero(percentualDoDesconto(desconto, q, pc), 2) })
      : '';
  const trocarTipo = (novo: TipoDesconto) => {
    if (novo === tipo) return;
    const convertido = converterDesconto(tipo, novo, digitado, q, pc);
    setValorDesconto(convertido ? numeroEditavel(convertido) : '');
    setTipo(novo);
    setErro(null);
  };
  const salvar = async (outro = false) => {
    if (!servico) return setErro(t('crm.ficha.produtos.escolha'));
    if (!(q > 0)) return setErro(t('crm.ficha.produtos.quantidadeInvalida'));
    if (limite) return setErro(t(`crm.ficha.produtos.limites.${limite.chave}`, { maximo: dinheiro(limite.maximo, op.moeda) }));
    setSalvando(true);
    try {
      await aoSalvar({
        id: atual?.id ?? crypto.randomUUID(),
        servico_id: servico.id,
        servico_nome: servico.nome,
        descricao: atual?.descricao ?? servico.nome,
        quantidade: q,
        preco_centavos: pc,
        recorrencia,
        vertical_id: verticalAtiva || null,
        desconto_tipo: comDesconto ? tipo : 'valor',
        desconto_percentual: comDesconto && tipo === 'percentual' ? digitado : null,
        desconto_centavos: comDesconto ? desconto : 0,
      }, outro);
    } catch (e) {
      toast.erro(mensagemDeErro(e));
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={atual ? t('crm.ficha.produtos.editarTitulo') : t('crm.ficha.produtos.adicionar')}
      rodape={
        passo === 1 ? (
          <>
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!servico || exigeVertical} onClick={() => setPasso(2)} data-avancar-produto="">
              {t('crm.ficha.produtos.avancar')}
            </Botao>
          </>
        ) : (
          <>
            <Botao variante="fantasma" onClick={() => (atual ? aoFechar() : setPasso(1))}>
              {atual ? tn('comum.cancelar') : t('crm.ficha.produtos.voltar')}
            </Botao>
            {/* Lista de 30/10 (B), item 16: grava, adiciona à negociação e volta ao passo 1 limpo (o primário segue sendo o "Adicionar"). */}
            {!atual && (
              <Botao variante="secundario" icone={<Plus className="size-4" />} disabled={salvando} onClick={() => void salvar(true)} data-salvar-produto-outro="">
                {t('crm.ficha.produtos.criarEOutro')}
              </Botao>
            )}
            <Botao variante="primario" carregando={salvando} onClick={() => void salvar()} data-salvar-produto="">
              {atual ? tn('comum.salvar') : t('crm.ficha.produtos.adicionarBotao')}
            </Botao>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4" data-form-produto="">
        <p className="text-sm">
          <span className={passo === 1 ? 'font-semibold text-acento-forte' : 'text-tinta-3'}>{t('crm.ficha.produtos.passo1')}</span>
          <span className="mx-2 text-tinta-3">›</span>
          <span className={passo === 2 ? 'font-semibold text-acento-forte' : 'text-tinta-3'}>{t('crm.ficha.produtos.passo2')}</span>
        </p>
        {passo === 1 ? (
          <CatalogoProdutos
            moeda={op.moeda}
            vertical={verticalAtiva}
            verticais={verticais}
            aoVertical={(v) => verticais.opcoes.some((e) => e.id === v) && trocarVertical(v)}
            aguardandoVertical={exigeVertical || !verticais.pronto}
            seletor={
              seletorVisivel ? (
                <SeletorVertical id={`${id}-vertical`} verticais={verticais} valor={verticalAtiva} atual={atual?.vertical_id ?? null} aoMudar={trocarVertical} />
              ) : verticais.unica && !atual ? (
                <p className="flex flex-wrap items-center gap-x-2 text-sm" data-vertical-unica="">
                  <span className="text-tinta-3">{t('crm.ficha2.vertical')}:</span>
                  <span className="font-semibold">{nomeDaEmpresa(verticais.unica)}</span>
                </p>
              ) : null
            }
            aviso={aviso}
            escolhido={servico?.id ?? null}
            aoEscolher={(x) => {
              setServico({ id: x.id, nome: x.nome, preco: x.preco_venda_centavos });
              setPreco(numeroEditavel(x.preco_venda_centavos / 100, 2));
              if (x.tipo_cobranca === 'recorrente' && recorrencia === 'avulso') setRecorrencia('mensal');
            }}
          />
        ) : (
          <>
            <div className="rounded-lg border border-borda bg-sup-1 px-3 py-2 text-sm">
              <p className="font-semibold">{servico?.nome}</p>
              <p className="text-xs text-tinta-3">{t('crm.ficha.produtos.precoBase', { valor: dinheiro(servico?.preco ?? 0, op.moeda) })}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Rotulo id={`${id}-qtd`} rotulo={t('crm.ficha.produtos.colunas.quantidade')} obrigatorio>
                <Entrada id={`${id}-qtd`} data-quantidade="" inputMode="decimal" value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
              </Rotulo>
              <Rotulo id={`${id}-preco`} rotulo={t('crm.ficha.produtos.valor')} obrigatorio>
                <EntradaSensivel mascara="dinheiro" rotulo={t('crm.ficha.produtos.valor')} id={`${id}-preco`} data-preco="" inputMode="decimal" value={preco} onChange={(e) => setPreco(e.target.value)} />
              </Rotulo>
            </div>
            <Rotulo id={`${id}-rec`} rotulo={t('itens.recorrenciaCampo')}>
              <Selecao id={`${id}-rec`} value={recorrencia} opcoes={RECORRENCIAS.map((r) => ({ valor: r, rotulo: t(`itens.recorrencia.${r}`) }))} onChange={(e) => setRecorrencia(e.target.value as Recorrencia)} />
            </Rotulo>
            {/* Val1 (28/10): na edição a vertical muda aqui (no cadastro novo, ela é o início do passo 1). */}
            {atual && seletorVisivel && <SeletorVertical id={`${id}-vertical`} verticais={verticais} valor={verticalAtiva} atual={atual.vertical_id ?? null} aoMudar={trocarVertical} />}
            <label className="flex items-center justify-between gap-3 text-sm font-semibold">
              {t('crm.ficha.produtos.acrescentarDesconto')}
              <Alternador marcado={comDesconto} aoMudar={setComDesconto} rotulo={t('crm.ficha.produtos.acrescentarDesconto')} />
            </label>
            {comDesconto && (
              <div className="flex flex-col gap-3 rounded-lg border border-borda p-3" data-desconto="">
                <div role="group" aria-label={t('crm.ficha.produtos.tipoDesconto')} className="flex rounded-lg border border-borda p-0.5">
                  {(['percentual', 'valor'] as const).map((x) => (
                    <button key={x} type="button" aria-pressed={tipo === x} data-tipo-desconto={x} onClick={() => trocarTipo(x)} className="flex-1 rounded-md px-3 py-2 text-sm font-medium text-tinta-2 aria-pressed:bg-acento-forte aria-pressed:text-sup-1">
                      {t(`crm.ficha.produtos.tipos.${x}`)}
                    </button>
                  ))}
                </div>
                <Rotulo id={`${id}-desc`} rotulo={t('crm.ficha.produtos.valorDesconto', { unidade: tipo === 'percentual' ? '%' : 'R$' })} erro={limite ? t(`crm.ficha.produtos.limites.${limite.chave}`, { maximo: dinheiro(limite.maximo, op.moeda) }) : undefined}>
                  <EntradaSensivel mascara={tipo === 'valor' ? 'dinheiro' : null} rotulo={t('crm.ficha.produtos.valorDesconto', { unidade: tipo === 'percentual' ? '%' : 'R$' })} id={`${id}-desc`} data-valor-desconto="" inputMode="decimal" value={valorDesconto} aria-invalid={!!limite || undefined} onChange={(e) => setValorDesconto(e.target.value)} />
                </Rotulo>
                {!limite && equivalencia && (
                  <p className="text-xs text-tinta-2" data-equivalencia="">
                    {equivalencia}
                  </p>
                )}
              </div>
            )}
            <dl className="flex flex-col gap-1 rounded-lg bg-sup-2 px-3 py-2 text-sm" data-resumo-produto="">
              <div className="flex justify-between">
                <dt>{t('crm.ficha.produtos.resumoBruto')}</dt>
                <dd className="numero">{dinheiro(total, op.moeda)}</dd>
              </div>
              <div className="flex justify-between">
                <dt>{t('crm.ficha.produtos.resumoDesconto')}</dt>
                <dd className="numero">{dinheiro(comDesconto && !limite ? desconto : 0, op.moeda)}</dd>
              </div>
              <div className="flex justify-between font-semibold">
                <dt>{t('crm.ficha.produtos.resumoLiquido')}</dt>
                <dd className="numero" data-liquido="">
                  {dinheiro(total - (comDesconto && !limite ? desconto : 0), op.moeda)}
                </dd>
              </div>
            </dl>
            {erro && <p className="text-sm text-perigo">{erro}</p>}
          </>
        )}
      </div>
    </Drawer>
  );
}

/**
 * Ajustes do CRM (27/10, F7), val1 (28/10) e crm8 (29/10, 2a): as verticais do item são as marcadas para o lead, a
 * negociação ou a empresa dela (`verticaisDoItem`). A da empresa vem de `/verticais/cadastros.parceiro/:id`; sem acesso a
 * ela (erro), conta como "Todas" e vale só a da negociação.
 */
function useVerticaisDoItem(op: Oportunidade) {
  const catalogo = useEmpresasDoCatalogo();
  const empresas = catalogo.data?.empresas ?? [];
  const marcacao = useQuery({
    queryKey: [`/verticais/pipeline.oportunidade/${op.id}`],
    queryFn: () => get<{ empresas: string[]; todas: boolean }>(`/verticais/pipeline.oportunidade/${op.id}`),
    enabled: empresas.length > 1,
  });
  const daEmpresa = useQuery({
    queryKey: [`/verticais/cadastros.parceiro/${op.parceiro_id}`],
    queryFn: () => get<{ empresas: string[]; todas: boolean }>(`/verticais/cadastros.parceiro/${op.parceiro_id}`),
    enabled: empresas.length > 1 && !!op.parceiro_id,
    retry: false,
  });
  const empresa = !op.parceiro_id || daEmpresa.isError ? { empresas: [], todas: true } : daEmpresa.data;
  // rl0311-3 SPEC-B2: `catalogo` diz se a lista de verticais já veio; sem ela o cadastro novo não sabe o que perguntar.
  return Object.assign(verticaisDoItem(empresas, marcacao.data, empresa), { catalogo });
}

/** Crm8 (29/10, 2a): a vertical do item, entre as do lead (a negociação primeiro, depois as da empresa, que viram "novas"). */
function SeletorVertical({ id, verticais, valor, atual, aoMudar }: { id: string; verticais: ReturnType<typeof verticaisDoItem<EmpresaCatalogo>>; valor: string; atual: string | null; aoMudar: (v: string) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation('nucleo');
  const opcoes = verticais.opcoes.map((e) => ({ valor: e.id, rotulo: verticais.nova(e.id) ? t('crm.ficha.produtos.verticalNova', { nome: nomeDaEmpresa(e) }) : nomeDaEmpresa(e) }));
  // O item antigo cuja vertical saiu do lead segue mostrando a dele (o servidor não reconfere o que já estava).
  if (atual && !opcoes.some((o) => o.valor === atual)) {
    const e = verticais.visiveis.find((x) => x.id === atual);
    opcoes.unshift({ valor: atual, rotulo: e ? nomeDaEmpresa(e) : tn('verticais.outra') });
  }
  return (
    <Rotulo id={id} rotulo={t('crm.ficha2.vertical')} ajuda={t(verticais.nova(valor) ? 'crm.ficha.produtos.verticalNovaAjuda' : 'crm.ficha2.verticalAjuda')} obrigatorio>
      <Selecao id={id} data-vertical-item="" value={valor} vazio={t('crm.ficha.produtos.escolhaVertical')} opcoes={opcoes} onChange={(e) => aoMudar(e.target.value)} />
    </Rotulo>
  );
}

/**
 * Ajustes do CRM (27/10, F6): o passo 1 do RD. Busca "por nome ou outras informações", a lista do catálogo de venda com o
 * rádio, o código e o tipo de cobrança, e o preço-base à direita; "Criar novo produto" (nome e preço-base) cria no catálogo
 * do Cadastros (com `cadastros.criar`) e já o escolhe.
 */
function CatalogoProdutos({
  moeda,
  vertical,
  verticais,
  aoVertical,
  aguardandoVertical,
  seletor,
  aviso,
  escolhido,
  aoEscolher,
}: {
  moeda: string;
  vertical: string;
  verticais: ReturnType<typeof useVerticaisDoItem>;
  /** Item 15: o produto criado numa vertical do lead passa a ser a vertical do item. */
  aoVertical: (v: string) => void;
  aguardandoVertical: boolean;
  seletor: React.ReactNode;
  aviso: string | null;
  escolhido: string | null;
  aoEscolher: (s: ServicoCatalogo) => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const toast = useToast();
  const id = useId();
  const [busca, setBusca] = useState('');
  const [criando, setCriando] = useState(false);
  // Lista de 03/11, item 8: a vertical do cadastro é sempre escolhível (entre todas as que a pessoa vê, ou "Todas"), já vindo
  // na vertical do item; uma fora do filtro da negociação cria o cadastro, não o escolhe, e a tela avisa.
  const [foraDoFiltro, setForaDoFiltro] = useState<string | null>(null);
  const catalogo = useCatalogoVenda();
  const podeCatalogo = catalogo.podeCatalogo;
  const gravar = useGravarServico(moeda);
  const norm = (x: string) => x.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const lista = servicosDaVertical(catalogo.data ?? [], vertical).filter((x) => !busca.trim() || norm(`${x.nome} ${x.codigo ?? ''}`).includes(norm(busca.trim())));
  /** O cadastro novo entra na lista do item? "Todas" sempre; senão, na vertical do item ou, sem ela, numa das do lead. */
  const entraNoFiltro = (destino: string) => destino === TODAS || !destino || (vertical ? destino === vertical : verticais.opcoes.length === 0 || verticais.opcoes.some((e) => e.id === destino));
  const criado = async (x: unknown, d: { vertical: string }) => {
    const novo = x as ServicoCatalogo;
    setCriando(false);
    if (!entraNoFiltro(d.vertical)) {
      const e = verticais.visiveis.find((v) => v.id === d.vertical);
      const aviso = t('crm.cfg.produtos.foraDoFiltro', { nome: textoLivre(novo.nome), vertical: e ? nomeDaEmpresa(e) : tn('verticais.outra') });
      setForaDoFiltro(aviso);
      toast.sucesso(t('crm.ficha2.produtoCriado'));
      return;
    }
    setForaDoFiltro(null);
    if (!vertical && d.vertical && d.vertical !== TODAS) aoVertical(d.vertical);
    aoEscolher(novo);
    toast.sucesso(t('crm.ficha2.produtoCriado'));
  };
  return (
    <div className="flex flex-col gap-3" data-catalogo-produtos="">
      {seletor}
      {aviso && (
        <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm" role="status" data-aviso-vertical="">
          {aviso}
        </p>
      )}
      {foraDoFiltro && (
        <p className="rounded-lg border border-borda bg-sup-2 px-3 py-2 text-sm" role="status" data-aviso-fora-do-filtro="">
          {foraDoFiltro}
        </p>
      )}
      <Rotulo id={`${id}-busca`} rotulo={t('crm.ficha2.escolhaCatalogo')}>
        <Entrada id={`${id}-busca`} type="search" value={busca} placeholder={t('crm.ficha2.buscarProduto')} onChange={(e) => setBusca(e.target.value)} data-buscar-produto="" />
      </Rotulo>
      {s.instalado('cadastros') && s.pode('cadastros.criar') && !criando && (
        <button type="button" className="flex min-h-11 w-fit items-center gap-1 text-sm font-medium text-acento-forte hover:underline md:min-h-9" onClick={() => { setForaDoFiltro(null); setCriando(true); }} data-criar-produto="">
          <Plus className="size-4" aria-hidden />
          {t('crm.ficha2.criarProduto')}
        </button>
      )}
      {criando && verticais.catalogo.isError && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-tinta-3" role="alert" data-verticais-erro="">
          <span>{mensagemDeErro(verticais.catalogo.error)}</span>
          <Botao tamanho="sm" variante="secundario" onClick={() => void verticais.catalogo.refetch()}>
            {tn('estados.tentarDeNovo')}
          </Botao>
        </div>
      )}
      {criando && (
        <FormServicoCatalogo
          visiveis={verticais.visiveis}
          verticalInicial={vertical}
          comCampos
          ajudaVertical={t('crm.cfg.produtos.verticalSempreAjuda')}
          confirmarDesabilitado={!verticais.pronto || !verticais.catalogo.isSuccess}
          rotuloConfirmar={t('crm.ficha2.criar')}
          aoCancelar={() => setCriando(false)}
          aoGravar={gravar.criar}
          aoGravado={criado}
        />
      )}
      <fieldset className="min-w-0 rounded-lg border border-borda">
        <legend className="sr-only">{t('crm.ficha2.todosProdutos')}</legend>
        <div className="flex justify-between border-b border-borda px-3 py-2 text-xs font-semibold tracking-wider text-tinta-3 uppercase" aria-hidden>
          <span className="pl-7">{t('crm.ficha2.todosProdutos')}</span>
          <span>{t('crm.ficha2.precoBase')}</span>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {!podeCatalogo && <p className="px-3 py-3 text-sm text-tinta-3">{tn('estados.semPermissao')}</p>}
          {podeCatalogo && aguardandoVertical && <p className="px-3 py-3 text-sm text-tinta-3" data-escolha-vertical="">{t('crm.ficha.produtos.escolhaVerticalPrimeiro')}</p>}
          {podeCatalogo && !aguardandoVertical && catalogo.isPending && <p className="px-3 py-3 text-sm text-tinta-3">{tn('comum.carregando')}</p>}
          {catalogo.isError && (
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 text-sm text-tinta-3" data-catalogo-erro="">
              <span>{mensagemDeErro(catalogo.error)}</span>
              <Botao tamanho="sm" variante="secundario" onClick={() => void catalogo.refetch()}>
                {tn('estados.tentarDeNovo')}
              </Botao>
            </div>
          )}
          {catalogo.isSuccess && !aguardandoVertical && lista.length === 0 && <p className="px-3 py-3 text-sm text-tinta-3">{t('crm.ficha2.nenhumProduto')}</p>}
          {!aguardandoVertical && lista.map((x) => (
            <label key={x.id} className="flex cursor-pointer items-center gap-3 border-b border-borda px-3 py-2 last:border-b-0 hover:bg-sup-2" data-produto-catalogo={x.nome}>
              <input type="radio" name={`${id}-produto`} checked={escolhido === x.id} onChange={() => aoEscolher(x)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold" title={textoLivre(x.nome)}>
                  {textoLivre(x.nome)}
                </span>
                <span className="block text-xs text-tinta-3">{[x.codigo, x.tipo_cobranca ? t(`crm.ficha2.cobranca.${x.tipo_cobranca === 'recorrente' ? 'recorrente' : 'avulso'}`) : null].filter(Boolean).join(' · ')}</span>
              </span>
              <span className="numero shrink-0 text-sm font-semibold">{dinheiro(x.preco_venda_centavos, moeda)}</span>
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  );
}
